import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Badge,
  Card,
  Row,
  Col,
  Form,
  InputGroup,
  Alert,
  Tab,
  Tabs,
} from "react-bootstrap";
import {
  Search,
  Edit,
  Eye,
  RefreshCw,
  Package,
  Calendar,
  Download,
} from "lucide-react";
import {
  getAllOrders,
  getOrdersByPartner,
  getOrdersByRetailer,
} from "../../api/orders";
import useAuth from "../../hooks/useAuth";
import { formatCurrency } from "../../utils/formatters";
import GenererPdfDevis from "./GenererPdfDevis";
import { createRoot } from "react-dom/client";

const OrderList = ({ onViewOrder, onCreateOrder }) => {
  const { isAdmin, isManager, isSalesRep, isDistributor, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [activeTab, setActiveTab] = useState("CLIENT");

  // Configuration de l'entreprise
  const company = {
    name: "SENEGAL SOPLAST SARL",
    address: {
      street: "Zone Industrielle de Thiès",
      city: "Thiès, Dakar, Sénégal",
    },
    phone1: "+221 33 888 88 88",
    email: "contact@soplast.sn",
  };

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError("");

        let data;
        if (isDistributor && user?.id) {
          data = await getOrdersByPartner(user.id);
        } else if (isSalesRep) {
          data = await getOrdersByRetailer();
        } else {
          data = await getAllOrders();
        }

        const safeData = Array.isArray(data) ? data : [];

        const sortedData = [...safeData].sort((a, b) => {
          const isPendingA = a.status === "PENDING" ? 1 : 0;
          const isPendingB = b.status === "PENDING" ? 1 : 0;
          if (isPendingB !== isPendingA) {
            return isPendingB - isPendingA;
          }
          return Number(b.id) - Number(a.id);
        });

        //const sortedData = (Array.isArray(data) ? data : []).sort((a, b) => b.id - a.id);


        setOrders(sortedData);
      } catch (err) {
        setError("Échec du chargement des commandes. Veuillez réessayer.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [refreshTrigger, isDistributor, isSalesRep, user?.id]);

  useEffect(() => {
    let filtered = Array.isArray(orders) ? [...orders] : [];

    if (searchTerm.trim() !== "") {
      filtered = filtered.filter(
        (order) =>
          order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.partner?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedStatus !== "ALL") {
      filtered = filtered.filter((order) => order.status === selectedStatus);
    }

    setFilteredOrders(filtered);
  }, [orders, searchTerm, selectedStatus]);

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  // --- Téléchargement du PDF (devis / commande) à la demande, façon InvoiceList ---
  // On ne génère le PDF qu'au clic, dans un noeud détaché, puis on nettoie.
  const handleDownloadDevis = (order) => {
    try {
      const pdfElement = document.createElement("div");
      pdfElement.style.position = "absolute";
      pdfElement.style.left = "-9999px";
      document.body.appendChild(pdfElement);

      const root = createRoot(pdfElement);
      root.render(
        <GenererPdfDevis
          order={order}
          company={company}
          onDownload={(blob) => {
            try {
              if (!(blob instanceof Blob)) {
                console.error("Error: Not a valid Blob object", blob);
                return;
              }
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              const docType = order.status === "PENDING" ? "devis" : "commande";
              link.download = `${docType}_${order.orderNumber}.pdf`;
              document.body.appendChild(link);
              link.click();

              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              document.body.removeChild(pdfElement);
              root.unmount();
            } catch (error) {
              console.error("Erreur lors du téléchargement du devis:", error);
            }
          }}
        />
      );
    } catch (err) {
      console.error("Erreur lors du téléchargement du devis:", err);
      setError("Impossible de télécharger le devis");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "PENDING":
        return <Badge bg="warning">En attente</Badge>;
      case "CONFIRMED":
        return <Badge bg="primary">Confirmé</Badge>;
      case "PROCESSING":
        return <Badge bg="info">En cours de traitement</Badge>;
      case "SHIPPED":
        return <Badge bg="purple">En cours de livraison</Badge>;
      case "DELIVERED":
        return <Badge bg="success">Livré</Badge>;
      case "CANCELLED":
        return <Badge bg="danger">Annulé</Badge>;
      case "REJECTED":
        return <Badge bg="dark">Rejeté</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getOrdersByPartnerType = (type) => {
    return filteredOrders.filter((order) => order.partner?.partnerType === type);
  };

  // --- Boutons d'action réutilisés (tableau + cartes) ---
  const renderActions = (order) => (
    <>
      <Button
        variant="outline-info"
        size="sm"
        className="me-1"
        onClick={() => onViewOrder(order.id)}
      >
        <Eye size={16} />
      </Button>

      {!isDistributor && (isAdmin || isManager) && order.status === "PENDING" && (
        <Button
          variant="outline-primary"
          size="sm"
          className="me-1"
          onClick={() => onViewOrder(order.id, true)}
        >
          <Edit size={16} />
        </Button>
      )}

      <Button
        variant="outline-secondary"
        size="sm"
        className="me-1"
        onClick={() => handleDownloadDevis(order)}
      >
        <Download size={16} />
      </Button>
    </>
  );

  // --- Vue tableau (grand écran) ---
  const renderOrdersTable = (list) => {
    return (
      <Table striped hover>
        <thead>
          <tr>
            <th>Commande #</th>
            {!isDistributor && (
              <th>{activeTab === "FOURNISSEUR" ? "Fournisseur" : "Client"}</th>
            )}
            {!isDistributor && <th>Créé par</th>}
            <th>Date</th>
            <th>Montant</th>
            <th>Statut</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {list.length > 0 ? (
            list.map((order) => (
                <tr key={order.id}>
                  <td>
                    <div className="fw-bold">{order.orderNumber}</div>
                    <small className="text-muted d-flex align-items-center">
                      <Package size={12} className="me-1" />
                      {order.items?.length ?? 0} Produits
                    </small>
                  </td>
                  {!isDistributor && (
                    <td>
                      <div>{order.partner?.name}</div>
                      <small className="text-muted">
                        {order.partner?.partnerType === "FOURNISSEUR" ? "" : "Client"}
                      </small>
                    </td>
                  )}
                  {!isDistributor && (
                    <td>
                      <small>
                        {order.createdBy?.fullName || order.createdBy?.username || "—"}
                      </small>
                    </td>
                  )}
                  <td>
                    <div className="small d-flex align-items-center">
                      <Calendar size={12} className="me-1" />
                      {formatDate(order.orderDate)}
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      <span>{formatCurrency(order.totalAmount)}</span>
                    </div>
                    {order.vatAmount > 0 && (
                      <small className="text-muted">
                        Incl. TVA: {formatCurrency(order.vatAmount)}
                      </small>
                    )}
                  </td>
                  <td>{getStatusBadge(order.status)}</td>
                  <td>{renderActions(order)}</td>
                </tr>
              ))
          ) : (
            <tr>
              <td colSpan={isDistributor ? "5" : "7"} className="text-center py-3">
                {isDistributor
                  ? "Aucune commande trouvée dans votre compte."
                  : "Aucune commande trouvée"}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    );
  };

  // --- Vue cartes (mobile / tablette) ---
  const renderOrdersCards = (list) => {
    if (!list || list.length === 0) {
      return (
        <div className="text-center text-muted py-4">
          {isDistributor
            ? "Aucune commande trouvée dans votre compte."
            : "Aucune commande trouvée"}
        </div>
      );
    }

    return list.map((order) => (
        <Card key={order.id} className="mb-2 shadow-sm">
          <Card.Body className="p-3">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <div className="fw-bold">{order.orderNumber}</div>
                <small className="text-muted d-flex align-items-center">
                  <Package size={12} className="me-1" />
                  {order.items?.length ?? 0} produits
                </small>
              </div>
              {getStatusBadge(order.status)}
            </div>

            {!isDistributor && (
              <div className="mb-2 small">
                <div>
                  <span className="text-muted">Client : </span>
                  {order.partner?.name || "—"}
                </div>
                <div>
                  <span className="text-muted">Créé par : </span>
                  {order.createdBy?.fullName || order.createdBy?.username || "—"}
                </div>
              </div>
            )}

            <div className="d-flex justify-content-between align-items-center mb-3">
              <small className="text-muted d-flex align-items-center">
                <Calendar size={12} className="me-1" />
                {formatDate(order.orderDate)}
              </small>
              <div className="text-end">
                <div className="fw-semibold">{formatCurrency(order.totalAmount)}</div>
                {order.vatAmount > 0 && (
                  <small className="text-muted">
                    Incl. TVA: {formatCurrency(order.vatAmount)}
                  </small>
                )}
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2">{renderActions(order)}</div>
          </Card.Body>
        </Card>
      ));
  };

  // --- Choisit la présentation selon la taille d'écran ---
  const renderOrders = (list) => (
    <>
      <div className="d-none d-lg-block">
        <div className="table-responsive">{renderOrdersTable(list)}</div>
      </div>
      <div className="d-lg-none">{renderOrdersCards(list)}</div>
    </>
  );

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <Row className="align-items-center g-2">
          <Col xs={12} sm>
            <h5 className="mb-0">
              {isDistributor ? "Mes Commandes" : "Gestion des commandes"}
            </h5>
          </Col>
          <Col xs={12} sm="auto">
            {!isDistributor && (
              <Button
                variant="success"
                size="sm"
                className="me-2"
                onClick={onCreateOrder}
              >
                <Package size={16} className="me-1" />
                Nouvelle commande
              </Button>
            )}
            <Button variant="outline-secondary" size="sm" onClick={handleRefresh}>
              <RefreshCw size={16} />
            </Button>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <Row className="mb-3 g-2">
          <Col xs={12} md={8}>
            <InputGroup>
              <InputGroup.Text>
                <Search size={18} />
              </InputGroup.Text>
              <Form.Control
                placeholder={
                  isDistributor
                    ? "Rechercher dans mes commandes..."
                    : "Rechercher par numéro de commande ou nom du client..."
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col xs={12} md={4}>
            <Form.Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="ALL">Tous les status</option>
              <option value="PENDING">En attente</option>
              <option value="CONFIRMED">Confirmé</option>
              <option value="PROCESSING">En cours de traitement</option>
              <option value="SHIPPED">En cours de livraison</option>
              <option value="DELIVERED">Livré</option>
              <option value="CANCELLED">Annulé</option>
              <option value="REJECTED">Rejeté</option>
            </Form.Select>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="mt-2">
              {isDistributor
                ? "Chargement de vos commandes..."
                : "Commandes en chargement..."}
            </p>
          </div>
        ) : isDistributor || isSalesRep ? (
          <>
            <h5 className="mb-3">Commandes</h5>
            {renderOrders(filteredOrders)}
          </>
        ) : (
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            <Tab eventKey="CLIENT" title="Commandes Clients">
              {renderOrders(getOrdersByPartnerType("CLIENT"))}
            </Tab>
            <Tab eventKey="FOURNISSEUR" title="Commandes Usine">
              {renderOrders(getOrdersByPartnerType("FOURNISSEUR"))}
            </Tab>
          </Tabs>
        )}
      </Card.Body>
    </Card>
  );
};

export default OrderList;